import { ComponentChild } from 'preact'
import Button from '../../control-components/ui-components/Button'
import Modal from '../../components/reactModal'
import RadioInputGroup from '../../control-components/ui-components/RadioInputGroup'
import Phrase from '../../libs/translationsUtils/phrase'
import EventCalendarRow from '../widget-events-calendar/EventCalendarRow'
import Icon from '../assets'
import Content from '../common/Content'
import FeaturedTopicsListAsRecommended from './FeaturedTopicsListAsRecommended'
import Share, { ShareButtonType } from '../../control-components/ui-components/Share'
import classnames from 'classnames'
import { getInsidedData } from '@insided/destination/utils/insidedDataStorage'

interface AttendeeStatus {
  isOpenForSignUp: boolean
  isAttendee: boolean
  isExternalRegistrationEnabled: boolean
  isPastEvent: boolean
}

interface Props {
  eventId: number
  title: string
  scheduleAt: string
  startAt: number
  endAt: number
  isOpenForSignUp: boolean
  isLoggedInUserAttendee: boolean
  image?: string
  type?: string
  confirmationMessage?: string
  isEventEngagementEnabled: boolean
  isPastEvent: boolean
  isAttendee: boolean
  isLoadingAttend: boolean
  showModalAttendConfirmation: boolean
  onCloseModalAttendConfirmation: () => void
  showModalEditRsvp: boolean
  rsvpStatus: boolean
  onAttend: () => void
  onEditRsvp: () => void
  onCloseModalEditRsvp: () => void
  onAttendanceChanged: (value: boolean) => void
  onUpdateRsvp: (e: Event) => void
  isLoadingConfirm: boolean
  isLoadingUpdate: boolean
  eventContent: string
  externalRegistrationUrl?: string
  externalRegistrationUrlLabel?: string
}

interface ModalProps {
  isLoading?: boolean
  modalTitle: ComponentChild
  buttonText: ComponentChild
}

interface AttendModalProps extends ModalProps{
  eventId: number
  showModalAttendConfirmation: boolean
  onCloseModalAttendConfirmation: () => void
  onAttend: () => void
  title: string
  startAt: number
  type?: string
  confirmationMessage?: string
  isLoadingConfirm: boolean
}

interface EditRsvpModalProps extends ModalProps{
  onEditRsvp: () => void
  showModalEditRsvp: boolean
  onCloseModalEditRsvp: () => void
  rsvpStatus: boolean
  onAttendanceChanged: (value: boolean) => void
  onUpdateRsvp: (e: Event) => void
  isLoadingUpdate: boolean
}

const types = [<Phrase p='events.attending' m='Forum' key='attending' />, <Phrase p='events.not.attending' m='Forum' key='not-attending' />]

const statusMapping = (attending: boolean) => {
  return (attending) ? types[0] : types[1]
}

const EventHeader = ({ title, scheduleAt, startAt, isOpenForSignUp, image, type, confirmationMessage, isEventEngagementEnabled, isAttendee, isLoadingAttend, showModalAttendConfirmation, onCloseModalAttendConfirmation, showModalEditRsvp, rsvpStatus, onAttend, onEditRsvp, onCloseModalEditRsvp, onUpdateRsvp, onAttendanceChanged, isLoadingConfirm, isPastEvent, isLoadingUpdate, eventId, eventContent, externalRegistrationUrl, externalRegistrationUrlLabel }: Props) => {
  const isRegisteredUser = typeof window === 'undefined' ? false : !!(getInsidedData('user')?.userid)
  const isExternalRegistrationEnabled = !!(externalRegistrationUrl && externalRegistrationUrlLabel)

  const externalRegistrationUrlClick = () => {
    if (typeof window === 'undefined') return

    window.open(externalRegistrationUrl, '_blank')
  }

  const socialShare = (
    <Share buttons={[{
      type: ShareButtonType.LINKEDIN,
      title: title,
      linkTitle: 'linkedin',
      content: eventContent
    }, {
      type: ShareButtonType.FACEBOOK,
      title: title,
      linkTitle: 'facebook',
      content: eventContent
    }, {
      type: ShareButtonType.TWITTER,
      title: title,
      linkTitle: 'twitter',
      content: eventContent
    }, {
      type: ShareButtonType.WHATSAPP,
      title: title,
      linkTitle: 'whatsapp',
      content: eventContent
    }]}
    />
  )

  const attendeeStatusMapping = {
    attend: <div className='event-cta-wrapper'>
      <Button
          // eslint-disable-next-line @gorazdo/preact/prefer-classname
        class='btn--cta btn--event event__sign-up qa-event-sign-up'
        onClick={() => onAttend()}
        loading={isRegisteredUser ? isLoadingAttend : false}
      >
        <Phrase p='events.attend' m='Forum' />
      </Button>
      {socialShare}
    </div>,
    Attending: <div className='event-attending-wrapper'>
      <div className='event-cta-wrapper'>
        <div className={classnames('event__attending', 'btn--event', 'qa-event-attending')}>
          <Icon customClass='icon-checkmark' icon='checkmark' />
          <span className='event__signed-up-text'><Phrase p='events.attending' m='Forum' /></span>
        </div>
        {socialShare}
      </div>
      <div>
        <a className={classnames('event__edit-rsvp', 'btn--event', 'qa-event-edit-rsvp')} onClick={() => onEditRsvp()}>
          <Phrase p='events.rsvp.edit' m='Forum' />
        </a>
      </div>
    </div>,
    attended: <div className='event-cta-wrapper'><EventAttendedButton />{socialShare}</div>,
    event_ended: <div className='event-cta-wrapper'><div className={classnames('btn--event', 'event__ended', 'qa-event-ended')}><Phrase p='events.ended' m='Forum' /></div>{socialShare}</div>,
    external_rsvp: <div className='event-cta-wrapper'>
      <Button
          // eslint-disable-next-line @gorazdo/preact/prefer-classname
        class='btn--cta btn--event event__sign-up qa-event-sign-up'
        onClick={() => externalRegistrationUrlClick()}
        loading={isRegisteredUser ? isLoadingAttend : false}
      >
        {externalRegistrationUrlLabel ?? 'Register'}
      </Button>
      {socialShare}
    </div>
  }
  const attendeeStatus = checkAttendeeStatus({ isOpenForSignUp, isAttendee, isExternalRegistrationEnabled, isPastEvent })
  return (
    <div className={`event-header-container ${image ? '' : 'event-header-container-full-width'}`}>
      <div className='event-header-wrapper'>
        <div className='event-header__title'>
          {type &&
            <span className={`event-pill__type pill pill--uppercase qa-event-type-pill ${isOpenForSignUp ? '' : 'event-pill__past'}`}>{type}</span>}
          <div className={`event__date ${isOpenForSignUp ? '' : 'event__date_past'}`}>{scheduleAt}</div>
          <h1 className='event__title'>{title}</h1>
        </div>
        {
          isEventEngagementEnabled && (
            <div className='event-engagement-wrapper'>
              {attendeeStatusMapping[attendeeStatus]}
              <ConfirmAttendModal
                eventId={eventId}
                showModalAttendConfirmation={showModalAttendConfirmation}
                onCloseModalAttendConfirmation={onCloseModalAttendConfirmation}
                onAttend={onAttend} title={title} startAt={startAt}
                type={type}
                confirmationMessage={confirmationMessage}
                modalTitle={<Phrase p='events.attending.you' m='Forum' />}
                buttonText={<Phrase p='events.ok' m='Forum' />}
                isLoadingConfirm={isLoadingConfirm}
              />
              <UpdateRsvpModal
                onEditRsvp={onEditRsvp}
                showModalEditRsvp={showModalEditRsvp}
                onCloseModalEditRsvp={onCloseModalEditRsvp}
                onUpdateRsvp={onUpdateRsvp}
                rsvpStatus={rsvpStatus}
                onAttendanceChanged={onAttendanceChanged}
                modalTitle={<Phrase p='events.rsvp.update.title' m='Forum' />}
                buttonText={<Phrase p='events.rsvp.update' m='Forum' />}
                isLoadingUpdate={isLoadingUpdate}
              />
            </div>)
}
      </div>
      {image &&
        <div className='event__image' style={{ backgroundImage: `url(${image})` }} />}
    </div>
  )
}

const ConfirmAttendModal = ({ modalTitle, buttonText, showModalAttendConfirmation, onCloseModalAttendConfirmation, title, startAt, type, confirmationMessage, isLoadingConfirm, eventId }: AttendModalProps) => {
  return (
    <div className='confirmation-modal-wrapper qa-confirmation-modal'>
      <Modal isOpen={showModalAttendConfirmation} onClose={onCloseModalAttendConfirmation}>
        <div className='confirmation-modal-title'>
          <h3>{modalTitle}</h3>
        </div>
        <div className='confirmation-modal-body'>
          <EventCalendarRow title={title} startAt={startAt} type={type} />
          {confirmationMessage && <div className='confirmation-modal-message'>
            <Content content={confirmationMessage || ''} className='event__confirmation-message' />
          </div>}
          <FeaturedTopicsListAsRecommended eventId={eventId} />
          <div className='confirmation-modal-button'>
            <Button
                // eslint-disable-next-line @gorazdo/preact/prefer-classname
              class='btn--confirm event__attend-confirmation qa-event-attend-confirmation'
              onClick={() => onCloseModalAttendConfirmation()} type='submit' loading={isLoadingConfirm}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const UpdateRsvpModal = ({ modalTitle, buttonText, showModalEditRsvp, onCloseModalEditRsvp, onUpdateRsvp, rsvpStatus, onAttendanceChanged, isLoadingUpdate }: EditRsvpModalProps) => {
  return (
    <div className='edit-rsvp-modal-wrapper'>
      <Modal isOpen={showModalEditRsvp} onClose={onCloseModalEditRsvp}>
        <div className='modal-title'>
          <h3>{modalTitle}</h3>
          <div>
            <form className='edit-rsvp__form' onSubmit={(e: Event) => { onUpdateRsvp(e) }}>
              <RadioInputGroup
                required
                name='type'
                options={types}
                value={statusMapping(rsvpStatus)}
                onChange={(value: any) => onAttendanceChanged(value === types[0])}
              />
              <Button
                  // eslint-disable-next-line @gorazdo/preact/prefer-classname
                class='btn--event event__update-rsvp qa-event-update-rsvp' loading={isLoadingUpdate} type='submit'
              >
                {buttonText}
              </Button>
            </form>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const checkAttendeeStatus = ({ isOpenForSignUp, isAttendee, isExternalRegistrationEnabled, isPastEvent }: AttendeeStatus) => {
  if (isExternalRegistrationEnabled && !isPastEvent) {
    return 'external_rsvp'
  }

  if (isOpenForSignUp) {
    return (isAttendee) ? 'Attending' : 'attend'
  }

  return (isAttendee) ? 'attended' : 'event_ended'
}

const EventAttendedButton = () => {
  return (
    <div className={classnames('btn--event', 'event__attended', 'event__signed-up-past', 'qa-event-attended')}>
      <Icon customClass='icon-checkmark' icon='checkmark' />
      <span className='event__attended-text'>
        <Phrase p='events.attended' m='Forum' />
      </span>
    </div>
  )
}
export default EventHeader
